import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import PageLayout from "components/PageLayout";
import OutlinedCard from "components/OutlinedCard";
import PageTitle from "components/PageTitle";
import DataTable, { ColumnOptions, SortDirection } from "components/DataTable";
import { SimpleOrder } from "models/SimpleOrder";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getOrderStatusPropColor, getOrderStatusString } from "utilities/orders";
import FilterMenu from "components/FilterMenu";
import { Link as RouterLink } from "react-router-dom";
import OrdersFilters from "./components/OrdersFilters";
import { DEFAULT_FILTERS, selectLastViewedOrder, SortBy, setLastViewedOrder } from "./ordersSlice";
import { useOrderSearch } from "pages/orders/useOrderSearch";
import MobileOrdersList from "./components/MobileOrdersList";
import CancelIcon from "@mui/icons-material/Cancel";

const renderDate = (date: string | null) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString();
};

const columns: ColumnOptions<SimpleOrder> = [
  {
    label: "Ordernummer",
    key: "ordernr",
    dense: true,
    renderCell: (row) => (
      <Link component={RouterLink} to={`/orders/view/${row.ordernr}`}>
        {row.ordernr}
      </Link>
    ),
  },
  { label: "Kund", key: "companyName" },
  {
    label: "Order ändrad",
    key: "updatedAt",
    align: "right",
    dense: true,
    defaultSortDirection: "desc",
    renderCell: (row) => renderDate(row.updatedAt),
  },
  {
    label: "Status ändrad",
    key: "lastStatusUpdate",
    align: "right",
    dense: true,
    defaultSortDirection: "desc",
    renderCell: (row) => renderDate(row.lastStatusUpdate),
  },
  {
    label: "Status",
    key: "orderStatus",
    align: "right",
    dense: true,
    renderCell: (row) => (
      <Chip
        size="small"
        color={getOrderStatusPropColor(row.orderStatus)}
        label={getOrderStatusString(row.orderStatus)}
      />
    ),
  },
] as const;

const Orders: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));

  const {
    search,
    filters,
    sort,
    activeFiltersCount,
    orders,
    isFetching,
    isLoading,
    setSearch,
    setFilters,
    setSort,
    fetchNextPage,
  } = useOrderSearch();

  const lastViewedOrder = useAppSelector(selectLastViewedOrder);

  // Scroll to the last viewed order
  useEffect(() => {
    if (isLoading || !lastViewedOrder) return;

    const rows = document.querySelectorAll("td");
    for (const row of rows) {
      if (row.textContent === lastViewedOrder) {
        row.scrollIntoView();
        dispatch(setLastViewedOrder(null));
        break;
      }
    }
  }, [dispatch, isLoading, lastViewedOrder]);

  return (
    <PageLayout>
      <PageTitle>Ordrar</PageTitle>

      <OutlinedCard>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <TextField
            label="Sök ordernr, företag"
            variant="filled"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: "100%", maxWidth: isLargeScreen ? "30ch" : "100%" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" hidden={!search}>
                    <IconButton onClick={(e) => setSearch("")} edge="end">
                      <CancelIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <FilterMenu
            activeFiltersCount={activeFiltersCount}
            onReset={() => setFilters(DEFAULT_FILTERS)}
            tooltip="Välj filter"
          >
            <Stack spacing={2}>
              <OrdersFilters filters={filters} onChange={setFilters} />
            </Stack>
          </FilterMenu>
        </Box>

        {isLargeScreen ? (
          <DataTable
            columns={columns}
            data={orders ?? []}
            getKey={(row) => row.ordernr}
            striped
            sort={sort}
            onSortChange={(sortBy, sortDir) => setSort(sortBy as SortBy, sortDir as SortDirection)}
            emptyMessage="Inga ordrar hittades"
          />
        ) : (
          <MobileOrdersList orders={orders} />
        )}

        {!isFetching && (
          <Box display="flex" justifyContent="center" mt={1}>
            <Button variant="text" onClick={() => fetchNextPage()} startIcon={<RefreshIcon />}>
              Hämta fler ordrar
            </Button>
          </Box>
        )}
      </OutlinedCard>
    </PageLayout>
  );
};

export default Orders;
